import { useState, useEffect, type FormEvent } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useUsersStore } from '@/stores/usersStore';
import { createUser, updateUser } from '@/api/users';
import type { UserScope } from '@/types/users';
import Spinner from '@/components/ui/Spinner';

type ScopeMode = 'all' | 'clients';

export default function UserFormModal({ onSaved }: { onSaved: () => void }) {
  const { selectedUser, customers, closeForm } = useUsersStore();
  const isEdit = !!selectedUser;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('viewer');
  const [scopeMode, setScopeMode] = useState<ScopeMode>('all');
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedUser) {
      setUsername(selectedUser.username || '');
      setNotificationEmail(selectedUser.notification_email || '');
      setDisplayName(selectedUser.display_name);
      setRole(selectedUser.role);
      const hasAllScope = selectedUser.scopes.some((s) => s.type === 'all');
      if (hasAllScope || selectedUser.scopes.length === 0) {
        setScopeMode('all');
        setSelectedClients([]);
      } else {
        setScopeMode('clients');
        setSelectedClients(
          selectedUser.scopes
            .filter((s) => s.type === 'client' && s.value)
            .map((s) => s.value!)
        );
      }
    }
  }, [selectedUser]);

  const filteredCustomers = customers.filter(
    (c) =>
      c.toLowerCase().includes(clientSearch.toLowerCase()) &&
      !selectedClients.includes(c)
  );

  const addClient = (client: string) => {
    setSelectedClients((prev) => [...prev, client]);
    setClientSearch('');
    setShowDropdown(false);
  };

  const removeClient = (client: string) => {
    setSelectedClients((prev) => prev.filter((c) => c !== client));
  };

  const buildScopes = (): UserScope[] => {
    if (scopeMode === 'all') {
      return [{ type: 'all', value: null }];
    }
    return selectedClients.map((c) => ({ type: 'client', value: c }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isEdit && (!username || !password)) {
      setError('Usuario y contraseña son requeridos');
      return;
    }
    if (!displayName) {
      setError('El nombre es requerido');
      return;
    }
    if (scopeMode === 'clients' && selectedClients.length === 0) {
      setError('Selecciona al menos un cliente');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateUser(selectedUser!.id, {
          display_name: displayName,
          notification_email: notificationEmail || undefined,
          role,
          scopes: buildScopes(),
        });
      } else {
        await createUser({
          username,
          password,
          display_name: displayName,
          role,
          notification_email: notificationEmail || undefined,
          scopes: buildScopes(),
        });
      }
      onSaved();
      closeForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={closeForm} />
      <div className="relative bg-bg-secondary border border-border-strong rounded-2xl w-full max-w-md mx-4 shadow-2xl animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <h2 className="text-base font-semibold text-text-primary">
            {isEdit ? 'Editar Usuario' : 'Crear Usuario'}
          </h2>
          <button
            onClick={closeForm}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Username - only on create */}
          {!isEdit && (
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">
                Usuario
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full h-10 px-3 bg-bg-tertiary border border-border-subtle rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple transition-colors"
                placeholder="nombre_usuario"
              />
            </div>
          )}

          {/* Password - only on create */}
          {!isEdit && (
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">
                Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-10 px-3 bg-bg-tertiary border border-border-subtle rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple transition-colors"
                placeholder="min. 6 caracteres"
              />
            </div>
          )}

          {/* Display Name */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Nombre
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full h-10 px-3 bg-bg-tertiary border border-border-subtle rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple transition-colors"
              placeholder="Nombre completo"
            />
          </div>

          {/* Notification Email */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Correo de notificaciones
            </label>
            <input
              type="email"
              value={notificationEmail}
              onChange={(e) => setNotificationEmail(e.target.value)}
              className="w-full h-10 px-3 bg-bg-tertiary border border-border-subtle rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple transition-colors"
              placeholder="usuario@empresa.com"
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Rol
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full h-10 px-3 bg-bg-tertiary border border-border-subtle rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-purple transition-colors"
            >
              <option value="viewer">Viewer</option>
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Scopes */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-2">
              Acceso a datos
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scopeMode === 'all'}
                  onChange={() => setScopeMode('all')}
                  className="accent-accent-purple"
                />
                <span className="text-sm text-text-primary">
                  Todo (sin restricciones)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scopeMode === 'clients'}
                  onChange={() => setScopeMode('clients')}
                  className="accent-accent-purple"
                />
                <span className="text-sm text-text-primary">
                  Clientes específicos
                </span>
              </label>
            </div>

            {/* Client selector */}
            {scopeMode === 'clients' && (
              <div className="mt-3 space-y-2">
                {/* Selected chips */}
                {selectedClients.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedClients.map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-purple/15 text-accent-purple text-xs rounded-full"
                      >
                        {c}
                        <button
                          type="button"
                          onClick={() => removeClient(c)}
                          className="hover:text-red-400 transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className="w-full h-9 px-3 bg-bg-tertiary border border-border-subtle rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple transition-colors"
                    placeholder="Buscar cliente..."
                  />
                  {showDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-bg-elevated border border-border-subtle rounded-lg shadow-xl max-h-40 overflow-y-auto z-10">
                      {filteredCustomers.slice(0, 20).map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => addClient(c)}
                          className="w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-bg-tertiary transition-colors flex items-center gap-2"
                        >
                          <Plus size={12} className="text-text-muted" />
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={closeForm}
              className="flex-1 h-10 border border-border-subtle rounded-lg text-sm text-text-secondary hover:bg-bg-tertiary transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 h-10 bg-accent-purple text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {saving ? <Spinner size="sm" /> : isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
